import { useEffect, useRef, useState } from "react";

/**
 * Subtle cursor spark trail — desktop only, non-scrolling.
 * Emits a fading ✦ symbol every few pixels of movement.
 * Disabled during scroll to keep scroll silky-smooth.
 */
export default function CursorSpark() {
    const [sparks, setSparks] = useState([]);
    const lastPos = useRef({ x: 0, y: 0 });
    const idRef = useRef(0);
    const enabledRef = useRef(true);
    const scrollingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    useEffect(() => {
        // Disable on touch devices and reduced-motion preference
        if (typeof window === "undefined") return;
        if (window.matchMedia("(pointer: coarse)").matches) {
            enabledRef.current = false;
            return;
        }
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            enabledRef.current = false;
            return;
        }

        const onScroll = () => {
            scrollingRef.current = true;
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(() => {
                scrollingRef.current = false;
            }, 140);
        };

        const onMove = (e) => {
            if (!enabledRef.current) return;
            if (scrollingRef.current) return; // no sparks during scroll
            const { clientX: x, clientY: y } = e;
            const dx = x - lastPos.current.x;
            const dy = y - lastPos.current.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 80) return; // less frequent
            lastPos.current = { x, y };
            const id = ++idRef.current;
            setSparks((s) => [
                ...s.slice(-2),
                { id, x, y, r: Math.random() * 40 - 20 },
            ]);
            setTimeout(() => {
                setSparks((s) => s.filter((sp) => sp.id !== id));
            }, 800);
        };
        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("scroll", onScroll);
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
    }, []);

    if (sparks.length === 0) return null;

    return (
        <div
            aria-hidden="true"
            style={{
                position: "fixed",
                inset: 0,
                pointerEvents: "none",
                zIndex: 60,
            }}
        >
            {sparks.map((s) => (
                <span
                    key={s.id}
                    className="itly-cursor-spark"
                    style={{
                        left: s.x,
                        top: s.y,
                        transform: `translate(-50%, -50%) rotate(${s.r}deg)`,
                    }}
                >
                    ✦
                </span>
            ))}
        </div>
    );
}
